import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Loader2, UserPlus, User, Phone, Unlink, Building2 } from 'lucide-react'

import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from '@/common/ui/alert-dialog'
import { Skeleton } from '@/common/ui/skeleton'

import { useMutationLinkPlayer, useMutationUnlinkPlayer } from '../../api/hooks'
import useQueryGetPlayerList from '@/modules/manage-player/api/hooks/useQueryGetPlayerList'

import type { components } from '@/schema'
import { PlayerQueryKeys } from '@/modules/manage-player/keys'

type PlayerResponse = components['schemas']['PlayerResponse']

const linkPlayerSchema = z
	.object({
		phone: z.string().default(''),
		username: z.string().default(''),
	})
	.superRefine((data, ctx) => {
		const phone = data.phone.trim()
		const username = data.username.trim()
		if (!phone && !username) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'กรุณากรอกเบอร์โทรหรือชื่อผู้ใช้',
				path: ['phone'],
			})
			return
		}
		if (phone && !/^0[0-9]{8,9}$/.test(phone)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'รูปแบบเบอร์โทรศัพท์ไม่ถูกต้อง',
				path: ['phone'],
			})
		}
	})

type LinkPlayerFormData = z.infer<typeof linkPlayerSchema>

interface LinkPlayerDialogProps {
	roomId: string
	currentPlayer?: PlayerResponse | null
	open: boolean
	onOpenChange: (open: boolean) => void
}

export function LinkPlayerDialog({
	roomId,
	currentPlayer,
	open,
	onOpenChange,
}: LinkPlayerDialogProps) {
	const linkPlayer = useMutationLinkPlayer(roomId)
	const unlinkPlayer = useMutationUnlinkPlayer(roomId)

	const form = useForm<LinkPlayerFormData>({
		resolver: zodResolver(linkPlayerSchema),
		defaultValues: {
			phone: '',
			username: '',
		},
	})

	const phoneValue = useWatch({
		control: form.control,
		name: 'phone',
	})
	const usernameValue = useWatch({
		control: form.control,
		name: 'username',
	})

	const phoneTrimmed = phoneValue?.trim() ?? ''
	const usernameTrimmed = usernameValue?.trim() ?? ''
	const isValidPhoneForSearch = /^0[0-9]{8,9}$/.test(phoneTrimmed)
	const isValidUsernameForSearch = usernameTrimmed.length >= 2
	// Prefer phone for preview when it is a valid Thai mobile pattern
	const previewSearchQuery = isValidPhoneForSearch
		? phoneTrimmed
		: isValidUsernameForSearch
			? usernameTrimmed
			: undefined

	const { data: playerSearchResult, isLoading: isSearching } =
		useQueryGetPlayerList(
			{
				skip: 0,
				take: 1,
				search: previewSearchQuery,
			},
			{
				enabled: !!previewSearchQuery,
				queryKey: [
					PlayerQueryKeys.GetPlayerList,
					{ search: previewSearchQuery },
				],
			},
		)

	const searchedPlayer = playerSearchResult?.data?.[0]

	const handleSubmit = async (data: LinkPlayerFormData) => {
		const trimmedPhone = data.phone.trim() || undefined
		const trimmedUsername = data.username.trim() || undefined
		try {
			await linkPlayer.mutateAsync({
				...(trimmedPhone ? { phone: trimmedPhone } : {}),
				...(trimmedUsername ? { username: trimmedUsername } : {}),
			})
			toast.success('เชื่อมต่อผู้เล่นสำเร็จ')
			form.reset()
			onOpenChange(false)
		} catch {
			toast.error('เชื่อมต่อล้มเหลว กรุณาตรวจสอบเบอร์โทรหรือชื่อผู้ใช้')
		}
	}

	const handleUnlink = async () => {
		try {
			await unlinkPlayer.mutateAsync()
			toast.success('ยกเลิกการเชื่อมต่อผู้เล่นสำเร็จ')
			onOpenChange(false)
		} catch {
			toast.error('ยกเลิกการเชื่อมต่อล้มเหลว')
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<UserPlus className="h-5 w-5" />
						เชื่อมต่อผู้เล่น
					</DialogTitle>
					<DialogDescription>
						เชื่อมต่อห้องแชทนี้กับบัญชีผู้เล่นโดยใช้เบอร์โทรศัพท์หรือชื่อผู้ใช้
						(username) — ถ้ากรอกทั้งสอง ระบบจะใช้เบอร์โทรเป็นหลัก
					</DialogDescription>
				</DialogHeader>

				{currentPlayer ? (
					<div className="space-y-4">
						{/* Current linked player info */}
						<div className="rounded-lg border bg-muted/50 p-4">
							<p className="mb-2 text-sm font-medium text-muted-foreground">
								ผู้เล่นที่เชื่อมต่ออยู่
							</p>
							<div className="space-y-1 text-sm">
								{currentPlayer.username ? (
									<p className="font-medium">{currentPlayer.username}</p>
								) : null}
								<p
									className={
										currentPlayer.username
											? 'text-muted-foreground'
											: 'font-medium'
									}
								>
									{currentPlayer.phone}
								</p>
							</div>
						</div>

						{/* Unlink confirmation */}
						<AlertDialog>
							<AlertDialogTrigger asChild>
								<Button
									variant="destructive"
									className="w-full"
									disabled={unlinkPlayer.isPending}
								>
									{unlinkPlayer.isPending ? (
										<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									) : (
										<Unlink className="mr-2 h-4 w-4" />
									)}
									ยกเลิกการเชื่อมต่อ
								</Button>
							</AlertDialogTrigger>
							<AlertDialogContent>
								<AlertDialogHeader>
									<AlertDialogTitle>ยืนยันการยกเลิก</AlertDialogTitle>
									<AlertDialogDescription>
										คุณต้องการยกเลิกการเชื่อมต่อผู้เล่น{' '}
										<strong>
											{currentPlayer.username
												? `${currentPlayer.username} (${currentPlayer.phone})`
												: currentPlayer.phone}
										</strong>{' '}
										จากห้องแชทนี้หรือไม่?
									</AlertDialogDescription>
								</AlertDialogHeader>
								<AlertDialogFooter>
									<AlertDialogCancel>ยกเลิก</AlertDialogCancel>
									<AlertDialogAction
										onClick={handleUnlink}
										className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
									>
										ยืนยัน
									</AlertDialogAction>
								</AlertDialogFooter>
							</AlertDialogContent>
						</AlertDialog>

						<div className="relative">
							<div className="absolute inset-0 flex items-center">
								<span className="w-full border-t" />
							</div>
							<div className="relative flex justify-center text-xs uppercase">
								<span className="bg-background px-2 text-muted-foreground">
									หรือเปลี่ยนผู้เล่น
								</span>
							</div>
						</div>
					</div>
				) : null}

				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(handleSubmit)}
						className="space-y-4"
					>
						<FormField
							control={form.control}
							name="phone"
							render={({ field }) => (
								<FormItem>
									<FormLabel>เบอร์โทรศัพท์</FormLabel>
									<FormControl>
										<div className="relative">
											<Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
											<Input
												placeholder="0812345678"
												className="pl-9"
												{...field}
											/>
										</div>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="username"
							render={({ field }) => (
								<FormItem>
									<FormLabel>ชื่อผู้ใช้</FormLabel>
									<FormControl>
										<div className="relative">
											<User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
											<Input
												placeholder="ชื่อผู้ใช้"
												className="pl-9"
												autoComplete="off"
												{...field}
											/>
										</div>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						{/* Player Preview */}
						{previewSearchQuery && (
							<div className="rounded-lg border bg-muted/30 p-3">
								{isSearching ? (
									<div className="space-y-2">
										<Skeleton className="h-4 w-24" />
										<Skeleton className="h-4 w-32" />
									</div>
								) : searchedPlayer ? (
									<div className="space-y-2">
										<p className="text-sm font-medium text-green-600">
											พบผู้เล่น
										</p>
										<div className="space-y-1 text-sm">
											<div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-muted-foreground">
												{searchedPlayer.username ? (
													<span className="inline-flex items-center gap-1">
														<User className="h-3.5 w-3.5 shrink-0" />
														{searchedPlayer.username}
													</span>
												) : null}
												{searchedPlayer.username ? (
													<span className="text-muted-foreground/70">·</span>
												) : null}
												<span className="inline-flex items-center gap-1">
													<Phone className="h-3.5 w-3.5 shrink-0" />
													{searchedPlayer.phone}
												</span>
											</div>
											{searchedPlayer.bank && (
												<div className="flex items-center gap-2 text-muted-foreground">
													<Building2 className="h-3.5 w-3.5" />
													<span>
														{searchedPlayer.bank.bankProvider} -{' '}
														{searchedPlayer.bank.accountName}{' '}
														{searchedPlayer.bank.accountNumber}
													</span>
												</div>
											)}
										</div>
									</div>
								) : (
									<p className="text-sm text-destructive">
										ไม่พบผู้เล่นจากข้อมูลที่ค้นหา
									</p>
								)}
							</div>
						)}

						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => onOpenChange(false)}
							>
								ยกเลิก
							</Button>
							<Button type="submit" disabled={linkPlayer.isPending}>
								{linkPlayer.isPending ? (
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								) : (
									<UserPlus className="mr-2 h-4 w-4" />
								)}
								เชื่อมต่อ
							</Button>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	)
}
